import {Text} from '@dropbox/dig-components/typography';
import {Box, Stack} from '@dropbox/dig-foundations';
import {WorkstreamBasicWithSummary} from 'client';
import {EmptyState} from 'components/DSYS/EmptyState';
import {tableStickyScrollContainerDataProps} from 'components/DSYS/tableStickyLayout';
import {t} from 'i18next';
import {useMemo} from 'react';
import {Trans} from 'react-i18next';
import {useWorkstreamDetails} from 'views/workstreams/detail/hooks';
import {isWorkstreamStatusArchived} from 'views/workstreams/utils';

import {MetricsScorecardControls} from './MetricsScorecardControls';
import type {MetricsScorecardControls as MetricsScorecardControlsState} from './useMetricsScorecardControls';
import {WorkstreamMetricsScorecardSection} from './WorkstreamMetricsScorecardSection';
import {WorkstreamMetricsScorecardTable} from './WorkstreamMetricsScorecardTable';

const hasMetrics = (metrics: WorkstreamBasicWithSummary['metrics'] | undefined | null) =>
  (metrics ?? []).some((metric) => metric.type === 'metric');

/**
 * Renders the "Metrics" view mode for a workstream list/pillar view: one read-only
 * metrics table per workstream. The shared year + grain (Annual/Quarterly) controls
 * live in the page toolbar; this component consumes their state via `controls`.
 * Each section lazily fetches its own forecasts + direct sub-workstream metrics.
 *
 * With `timeframeInHeader`, the timeframe menu moves into the table's column header
 * instead, and Annual renders as one grouped table too, so it has a header to hold it.
 */
export const WorkstreamMetricsScorecardList = ({
  workstreams,
  controls,
  includeArchived = false,
  timeframeInHeader = false,
  v4Analytics = false,
  scopeName,
}: {
  workstreams: WorkstreamBasicWithSummary[];
  controls: MetricsScorecardControlsState;
  includeArchived?: boolean;
  timeframeInHeader?: boolean;
  /** Tag the header timeframe menu's events `v4: true` (/workstreams). */
  v4Analytics?: boolean;
  /** The selected place (My workstreams, a pillar, ...), named in the empty state. */
  scopeName?: string;
}) => {
  const {grain, quarter, effectiveYear, selectedPeriod} = controls;

  // Sections skip workstreams with no metrics of their own or on a direct
  // sub-workstream. The sub-workstream check reads the same detail queries the
  // sections fetch, so it costs no extra requests.
  const parentIds = useMemo(
    () =>
      workstreams
        .filter((workstream) => (workstream.dependencies_count ?? 0) > 0)
        .map(({id}) => id),
    [workstreams]
  );
  const detailQueries = useWorkstreamDetails(parentIds);
  const hasOwnMetrics = workstreams.some((workstream) => hasMetrics(workstream.metrics));
  const isEmpty =
    !hasOwnMetrics &&
    detailQueries.every(
      ({data, isPending}) =>
        !isPending &&
        !(data?.dependencies ?? []).some(
          ({dependent}) =>
            (includeArchived || !isWorkstreamStatusArchived(dependent)) &&
            hasMetrics(dependent.metrics)
        )
    );

  if (workstreams.length === 0) {
    return (
      <Box paddingY="24" display="flex" justifyContent="center">
        <Text size="small" color="subtle">
          {t('no_workstreams')}
        </Text>
      </Box>
    );
  }

  if (isEmpty) {
    return (
      <Box paddingX="16">
        <EmptyState
          title={t('workstreams_metrics_empty')}
          body={
            scopeName ? (
              <Trans
                i18nKey="workstreams_metrics_empty_body_scoped"
                t={t}
                values={{name: scopeName}}
                components={{Name: <Text isBold />}}
              />
            ) : (
              t('workstreams_metrics_empty_body')
            )
          }
          image={null}
        />
      </Box>
    );
  }

  return (
    <Box
      overflowX="hidden"
      overflowY="auto"
      style={{height: '100%', minHeight: 0}}
      {...tableStickyScrollContainerDataProps}
    >
      {grain === 'quarterly' || timeframeInHeader ? (
        <WorkstreamMetricsScorecardTable
          grain={grain}
          workstreams={workstreams}
          year={effectiveYear}
          quarter={quarter}
          includeArchived={includeArchived}
          headerControl={
            timeframeInHeader ? (
              <MetricsScorecardControls
                controls={controls}
                triggerSize="small"
                v4Analytics={v4Analytics}
              />
            ) : undefined
          }
        />
      ) : (
        <Stack gap="16">
          {workstreams.map((workstream) => (
            <WorkstreamMetricsScorecardSection
              key={workstream.id}
              workstream={workstream}
              selectedPeriod={selectedPeriod}
              year={effectiveYear}
              grain="annual"
              quarter={quarter}
              includeArchived={includeArchived}
            />
          ))}
        </Stack>
      )}
    </Box>
  );
};
